import { isSupportedImageUrl } from '@/lib/media/image-support';
import type { MediaItem, Tweet } from '@/types/tweet';

export interface SummaryImage {
  altText?: string | null;
  url: string;
}

export const MAX_TWEET_SUMMARY_IMAGES = 5;
export const MAX_CONVERSATION_SUMMARY_IMAGES = 5;
const HTTP_URL_PATTERN = /^https?:\/\//i;

function normalizeUrl(candidate: unknown): string | null {
  if (typeof candidate !== 'string') {
    return null;
  }
  const trimmed = candidate.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (!HTTP_URL_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed;
}

function extractImageCandidates(item: MediaItem | null | undefined): SummaryImage[] {
  if (!item) {
    return [];
  }

  const candidates: SummaryImage[] = [];

  if (item.type === 'photo') {
    candidates.push({ url: item.url ?? '', altText: item.altText ?? null });
  } else if (item.type === 'video' || item.type === 'animated_gif') {
    candidates.push({ url: item.previewImageUrl ?? '', altText: item.altText ?? null });
  } else {
    candidates.push(
      { url: item.url ?? '', altText: item.altText ?? null },
      { url: item.previewImageUrl ?? '', altText: item.altText ?? null }
    );
  }

  return candidates
    .map((candidate) => ({ ...candidate, url: normalizeUrl(candidate.url) ?? '' }))
    .filter((candidate) => candidate.url.length > 0);
}

export function collectTweetSummaryImages(tweet: Pick<Tweet, 'mediaItems'> | null | undefined): SummaryImage[] {
  if (!(tweet && Array.isArray(tweet.mediaItems))) {
    return [];
  }

  const images: SummaryImage[] = [];
  const seen = new Set<string>();

  for (const mediaItem of tweet.mediaItems) {
    for (const candidate of extractImageCandidates(mediaItem)) {
      if (!isSupportedImageUrl(candidate.url)) {
        continue;
      }
      if (seen.has(candidate.url)) {
        continue;
      }
      seen.add(candidate.url);
      images.push(candidate);
      break;
    }
  }

  return images;
}

export function collectConversationSummaryImages<T extends Pick<Tweet, 'mediaItems'>>(
  tweetIds: readonly string[],
  tweets: Record<string, T | undefined>,
  max: number = MAX_CONVERSATION_SUMMARY_IMAGES
): SummaryImage[] {
  const images: SummaryImage[] = [];
  const seen = new Set<string>();

  for (const id of tweetIds) {
    const tweet = tweets[id];
    if (!tweet) {
      continue;
    }

    for (const image of collectTweetSummaryImages(tweet)) {
      if (seen.has(image.url)) {
        continue;
      }
      seen.add(image.url);
      images.push(image);
      if (images.length >= max) {
        return images;
      }
    }
  }

  return images;
}

export function limitSummaryImages(images: SummaryImage[], max: number): SummaryImage[] {
  if (images.length <= max) {
    return images;
  }
  return images.slice(0, max);
}
